import React, { useEffect, useRef, useState } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';
import { langColor, langFromPath } from './codeBlockUtils';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

function basename(p: string) {
  return p.split('/').filter(Boolean).pop() ?? p;
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.75" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
    </svg>
  );
}

function ScrollIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5.5 3.5C3.5 3.5 3.5 1.5 5.5 1.5H14.5C16.5 1.5 16.5 3.5 14.5 3.5" />
      <rect x="4" y="3.5" width="12" height="13" rx="0.75" />
      <path d="M5.5 16.5C3.5 16.5 3.5 18.5 5.5 18.5H14.5C16.5 18.5 16.5 16.5 14.5 16.5" />
      <line x1="7" y1="7" x2="13" y2="7" /><line x1="7" y1="10" x2="13" y2="10" /><line x1="7" y1="13" x2="11" y2="13" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

/** Derive a human-readable scope label and accent color from the path */
function scopeInfo(
  t: TFunction<'pleri/chat'>,
  path: string,
): {
  label: string;
  sublabel: string;
  iconColor: string;
  Icon: React.FC<{ className?: string }>;
} {
  if (path.startsWith('skill://')) {
    const name = path.slice('skill://'.length).replace(/\/+$/, '').split('/')[0];
    return { label: t('skill'), sublabel: name, iconColor: 'text-[#FF732E]', Icon: ScrollIcon };
  }
  if (path.startsWith('session://')) {
    const name = path.slice('session://'.length).replace(/\/+$/, '').split('/')[0];
    return { label: t('session'), sublabel: name, iconColor: 'text-blue-400', Icon: FolderIcon };
  }
  return {
    label: t('files'),
    sublabel: t('currentSession'),
    iconColor: 'text-gray-400',
    Icon: FolderIcon,
  };
}

export const ListFilesBlock: React.FC<{ content: ToolUseBlock }> = ({ content }) => {
  const { t } = useTranslation('pleri/chat');
  const [open, setOpen] = useState(true);
  const userInteracted = useRef(false);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (s.conversations[activeConversationId]?.pendingToolUses ?? new Set()).has(content.id)
      : false,
  );
  const resultJson = useToolResultContent(content.id);
  const isRunning = isPending || !resultJson;

  const inputPath = typeof content.input?.path === 'string' ? content.input.path : '';
  const inputGlob = typeof content.input?.glob === 'string' ? content.input.glob : '';
  const { label, sublabel, iconColor, Icon } = scopeInfo(t, inputPath);

  useEffect(() => {
    if (userInteracted.current) return;
    if (!isRunning) {
      const t = setTimeout(() => setOpen(false), 1200);
      return () => clearTimeout(t);
    }
  }, [isRunning]);

  let files: string[] = [];
  let isEmpty = false;

  if (resultJson) {
    const raw = (() => {
      try { return JSON.parse(resultJson) as string; } catch { return resultJson; }
    })();
    if (typeof raw === 'string') {
      isEmpty = /empty|no files/i.test(raw);
      files = raw.split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0 && !l.match(/^\d+ file/) && !l.match(/^Current|^session:|^skill:/));
    }
  }

  const fileCount = files.length;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950 shadow-sm">
      {/* Clean header — no terminal chrome */}
      <button
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-900/40 transition-colors"
        onClick={() => { userInteracted.current = true; setOpen((v) => !v); }}
      >
        <Icon className={`h-4 w-4 flex-shrink-0 ${iconColor}`} />

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-200">{label}</span>
            {inputGlob && (
              <span className="rounded bg-gray-800 px-1.5 py-px font-mono text-xs text-gray-500">{inputGlob}</span>
            )}
          </div>
          <span className="font-mono text-xs text-gray-600">{sublabel}</span>
        </div>

        {!isRunning && !isEmpty && fileCount > 0 && (
          <span className="flex-shrink-0 rounded-md bg-gray-800 px-1.5 py-0.5 text-xs font-medium text-gray-500">
            {fileCount}
          </span>
        )}

        {isRunning ? (
          <svg className="h-3 w-3 animate-spin flex-shrink-0 text-yellow-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <span className={`flex-shrink-0 text-gray-600 transition-colors hover:text-gray-400 ${fileCount === 0 ? 'invisible' : ''}`}>
            <ChevronIcon open={open} />
          </span>
        )}
      </button>
      {/* File list */}
      {!isRunning && open && (
        <div className="border-t border-gray-800/60">
          {isEmpty ? (
            <div className="px-3 py-2 text-xs text-gray-600">
              {t('noFilesFound')}
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto py-0.5">
              {files.map((path, i) => {
                const name = basename(path);
                const color = langColor(langFromPath(name));
                const isDir = path.endsWith('/');
                return (
                  <div key={i} className="flex items-center gap-2.5 px-3 py-[3.5px] hover:bg-gray-900/50 transition-colors">
                    <span
                      className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: isDir ? '#374151' : color }}
                    />
                    <span className={`flex-1 truncate font-mono text-xs ${isDir ? 'text-gray-500' : 'text-gray-300'}`}>
                      {path}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
